import { useCallback } from 'react'
import useProjectId from '../../customHooks/useProjectId'
import { useAccessToken } from '../useAccessToken'
import { useHandleError } from '../useHandleError'
import { makeResultFetch } from '../genericFetch'
import { apiRequestParams } from '../fetchOptions'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PeriodReportData } from '../queries/PeriodReportData'

export const useUpdateProjectForecast = (reportGuid: string | null) => {
  const getAccessToken = useAccessToken()
  const handleError = useHandleError()
  const projectId = useProjectId()
  const queryClient = useQueryClient()

  const mutationFunction = useCallback(async () => {
    if (!reportGuid) {
      throw new Error('Report GUID is required')
    }

    return getAccessToken().then((accessToken) => {
      if (!accessToken) throw new Error('Access token is undefined')

      const fetchedData = makeResultFetch<PeriodReportData>(
        `/reports/${projectId}/projectForecast/${reportGuid}/update`,
        apiRequestParams(accessToken, 'POST')
      )
        .then((response) => {
          const { data, message, success } = response

          if (!success) throw Error(message)
          if (!data) throw Error('No data returned from update')

          return data
        })
        .catch((error) => {
          throw error
        })

      return fetchedData
    })
  }, [getAccessToken, projectId, reportGuid])

  const handleSuccess = (updatedReport: PeriodReportData) => {
    // Set the updated report directly in its own cache
    queryClient.setQueryData(
      [projectId, 'projectForecastReport', reportGuid],
      updatedReport
    )

    // Update the period reports list cache directly with the returned data
    queryClient.setQueryData(
      [projectId, 'periodReportSnapshots'],
      (oldData: PeriodReportData[] | undefined) => {
        if (!oldData) return oldData
        return oldData.map((report) =>
          report.id === updatedReport.id ? updatedReport : report
        )
      }
    )

    // Invalidate all other individual projectForecast report caches since their
    // snapshot references may also have been updated
    queryClient.invalidateQueries({
      queryKey: [projectId, 'projectForecastReport'],
      predicate: (query) => query.queryKey[2] !== reportGuid,
    })

    // Invalidate the project forecast reports list so new data is fetched
    queryClient.invalidateQueries({
      queryKey: [projectId, 'projectForecastReports'],
    })

    // Invalidate all snapshot caches so new references are fetched fresh
    queryClient.invalidateQueries({
      queryKey: ['periodReportSnapshot', projectId],
    })

    // Refetch live cashflow and performance data immediately
    queryClient.refetchQueries({ queryKey: [projectId, 'cashflow'] })
    queryClient.refetchQueries({ queryKey: [projectId, 'performance'] })
  }

  const mutation = useMutation<PeriodReportData, Error>({
    mutationFn: mutationFunction,
    onSuccess: handleSuccess,
    onError: handleError,
    networkMode: 'always',
  })

  return mutation
}
